// @flow
import React, { Fragment } from 'react';
import { Watch } from 'scrollmonitor-react';
import FocusedImage from 'Components/FocusedImage';
import { Container, Row, Col } from 'Components/Grid';
import AssetModel from 'Stores/AssetModel';
import styles from './styles.scss';
import classNames from 'classnames/bind';
import ConsumptionData from 'Components/ConsumptionData';
import Icon from 'Components/Icon';

const cx = classNames.bind(styles);

type Props = {
  fullsize: boolean,
  autoplay: boolean,
  background: string,
  poster: { src: string, dim: number, blur: boolean },
  sources: [{ src: string, type: string }],
  isInViewport: boolean,
  deeplinkId?: number,
  ariaHidden?: boolean,
};

type State = {
  playing: boolean,
  sources: [{ src: string, type: string }],
};

class Video extends React.Component<Props, State> {
  static defaultProps = {
    ariaHidden: true,
  };

  constructor(props: Props) {
    super(props);

    const currentSourceType = window.innerWidth > 800 ? 'hi_res' : 'low_res';
    const deviceSources = props.sources.reduce((acc, source) => {
      if (source.type !== currentSourceType) {
        return acc;
      }

      return [...acc, source];
    }, []);

    this.state = {
      playing: false,
      // Check if we have device optimized sources. If not use all sources
      sources: deviceSources.length > 0 ? deviceSources : props.sources,
    };
  }

  UNSAFE_componentWillReceiveProps = (props) => {
    if (props.autoplay) {
      if (props.isInViewport) {
        this.play();
      } else {
        this.pause();
      }
    }
  };

  video: HTMLVideoElement;

  setRef = (video: HTMLVideoElement) => {
    this.video = video;
  };

  play = () => {
    if (this.video) {
      this.video.play();
    }
  };

  pause = () => {
    if (this.video) {
      this.video.pause();
    }
  };

  onPlayClick = () => {
    this.play();
  };

  onPlay = () => {
    this.setState({ playing: true });
  };

  onPause = () => {
    this.setState({ playing: false });
  };

  renderVideo = () => {
    const { ariaHidden, poster, autoplay, deeplinkId, consumptionText } = this.props;
    const { playing, sources } = this.state;
    const posterImage = poster ? new AssetModel({ ...poster, dim: 0.5, blur: false }) : null;

    const showOverlay = !playing && poster;

    const castAriaHidden = [true, false].includes(ariaHidden) ? String(ariaHidden) : undefined;

    return (
      <Fragment>
        <div className={styles.videoWrapper} id={deeplinkId} aria-hidden={castAriaHidden}>
          <div className={styles.videoContent}>
            {showOverlay && (
              <div className={styles.overlay} onClick={this.onPlayClick}>
                <FocusedImage image={posterImage} />
                <button className={styles.button} tabIndex={ariaHidden ? -1 : undefined}>
                  <Icon name="play" inline={false} className={styles.icon} />
                </button>
              </div>
            )}
            <video
              ref={this.setRef}
              controls={!autoplay}
              autoPlay={autoplay}
              loop={autoplay}
              muted={autoplay}
              playsInline={autoplay}
              onClick={this.onPause}
              onPlay={this.onPlay}
              onPause={this.onPause}
            >
              {sources.map((src, i) => {
                return <source key={src.src + i} src={src.src} />;
              })}
            </video>
          </div>
        </div>
        <ConsumptionData consumptionText={[consumptionText]} />
      </Fragment>
    );
  };

  render() {
    const { fullsize, background, deeplinkId } = this.props;

    if (fullsize) {
      return this.renderVideo();
    }

    const containerClass = cx(background, {
      withPadding: !fullsize,
    });

    return (
      <div className={containerClass} id={deeplinkId}>
        <Container>
          <Row>
            <Col>{this.renderVideo()}</Col>
          </Row>
        </Container>
      </div>
    );
  }
}

export default Watch(Video);
